'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Check, FlaskConical, Lightbulb, RotateCcw, X } from 'lucide-react';
import {
  ADRES_AKVARIUMA,
  OPYTY,
  itogOpyta,
  putOpyta,
  razobratSoobshchenie,
  type Opyt,
  type Sobytie,
} from '@/lib/kursy/akvarium';
import { AisLink as Link } from '@/components/aisearch/ais-link';

/**
 * Опыт в аквариуме: вопрос → предсказание → аквариум → результат.
 *
 * Аквариум открывается только после предсказания: предсказание, сделанное
 * после того, как увидел ответ, ничему не учит. Результат страница меряет
 * сама по событиям аквариума (postMessage из фрейма) и сравнивает с
 * предсказанием. Что человек ответил и чем кончилось, хранится в браузере.
 */

const KLUCH = 'ais-akvarium-opyty';
const GLAVNAYA =
  'inline-flex items-center gap-2 rounded-full bg-[var(--color-accent-500)] px-5 py-2.5 font-semibold text-[var(--color-bg)] hover:bg-[var(--color-accent-400)] disabled:opacity-50';
const VTORAYA =
  'inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] px-5 py-2.5 hover:border-[var(--color-accent-500)]';

interface Zapis {
  predskazanie: string;
  verno?: string;
}

export function prochitatOpyty(): Record<string, Zapis> {
  try {
    return JSON.parse(localStorage.getItem(KLUCH) ?? '{}') as Record<string, Zapis>;
  } catch {
    return {};
  }
}

function sohranit(slug: string, z: Zapis): void {
  try {
    localStorage.setItem(KLUCH, JSON.stringify({ ...prochitatOpyty(), [slug]: z }));
  } catch {
    // Без хранилища опыт идёт так же, только не запомнится.
  }
}

/** Новый seed на каждый запуск: аквариумы разные, как и обещано в курсе. */
function novyjSeed(): number {
  return Math.floor(Math.random() * 100000) + 1;
}

export function OpytAkvariuma({ opyt }: { opyt: Opyt }) {
  const [predskazanie, setPredskazanie] = useState<string | null>(null);
  const [nachat, setNachat] = useState(false);
  const [sobytiya, setSobytiya] = useState<Sobytie[]>([]);
  const [seed, setSeed] = useState(0);
  const frejm = useRef<HTMLIFrameElement>(null);
  /** Высота аквариума: фрейм растёт под неё, чтобы не было второй прокрутки внутри. */
  const [vysota, setVysota] = useState<number | null>(null);
  const nablyudatel = useRef<ResizeObserver | null>(null);

  const podognatVysotu = () => {
    nablyudatel.current?.disconnect();
    try {
      // Аквариум на том же сайте, поэтому его документ доступен напрямую.
      const d = frejm.current?.contentDocument;
      if (!d) return;
      const izmerit = () => {
        setVysota(d.documentElement.scrollHeight);
      };
      izmerit();
      nablyudatel.current = new ResizeObserver(izmerit);
      nablyudatel.current.observe(d.body);
    } catch {
      // Чужой адрес или запрет браузера: остаётся высота по умолчанию.
    }
  };

  useEffect(
    () => () => {
      nablyudatel.current?.disconnect();
    },
    [],
  );

  useEffect(() => {
    const z = prochitatOpyty()[opyt.slug];
    if (z) setPredskazanie(z.predskazanie);
  }, [opyt.slug]);

  useEffect(() => {
    if (!nachat) return;
    const slushat = (e: MessageEvent) => {
      if (e.source !== frejm.current?.contentWindow) return;
      const s = razobratSoobshchenie(e.data);
      if (!s || s.tip === 'drugoe') return;
      // «Заново» внутри аквариума начинает новый мир: и счёт с нуля.
      setSobytiya((pred) => (s.tip === 'start' ? [s] : [...pred, s]));
    };
    window.addEventListener('message', slushat);
    return () => {
      window.removeEventListener('message', slushat);
    };
  }, [nachat]);

  const itog = useMemo(() => itogOpyta(opyt, sobytiya), [opyt, sobytiya]);

  const blokItoga = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!itog.gotovo) return;
    if (predskazanie) sohranit(opyt.slug, { predskazanie, verno: itog.verno });
    // Результат ниже аквариума, а человек смотрит на аквариум: показываем
    // результат сами, один раз, когда опыт сделан.
    const t = setTimeout(() => {
      blokItoga.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 1200);
    return () => {
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itog.gotovo]);

  const zapustit = () => {
    if (!predskazanie) return;
    sohranit(opyt.slug, { predskazanie });
    setSobytiya([]);
    setSeed(novyjSeed());
    setNachat(true);
  };

  const sovpalo = itog.gotovo && itog.verno === predskazanie;
  const tekstPredskazaniya = opyt.varianty.find((v) => v.id === predskazanie)?.tekst;
  const tekstVernogo = opyt.varianty.find((v) => v.id === itog.verno)?.tekst;
  const sleduyushchij = OPYTY[OPYTY.findIndex((o) => o.slug === opyt.slug) + 1];

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-[var(--color-border)] p-5 sm:p-7">
        <p className="mb-1 text-sm font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
          1. Вопрос
        </p>
        <p className="mb-5 text-xl font-semibold">{opyt.vopros}</p>
        <p className="mb-3 text-sm text-[var(--color-text-muted)]">
          Выберите ответ до того, как откроете аквариум.
        </p>
        <div className="grid gap-2">
          {opyt.varianty.map((v) => (
            <label
              key={v.id}
              className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[var(--color-accent-500)] ${
                predskazanie === v.id
                  ? 'border-[var(--color-accent-500)] bg-[var(--color-surface)]'
                  : 'border-[var(--color-border)] hover:border-[var(--color-accent-500)]'
              } ${nachat ? 'pointer-events-none opacity-80' : ''}`}
            >
              <input
                type="radio"
                id={`predskazanie-${v.id}`}
                name="predskazanie"
                className="h-4 w-4"
                checked={predskazanie === v.id}
                disabled={nachat}
                onChange={() => {
                  setPredskazanie(v.id);
                }}
              />
              {v.tekst}
            </label>
          ))}
        </div>
        {!nachat && (
          <button
            type="button"
            onClick={zapustit}
            disabled={!predskazanie}
            className={`${GLAVNAYA} mt-5`}
          >
            <FlaskConical className="h-5 w-5" /> Записать предсказание и открыть аквариум
          </button>
        )}
      </section>

      {nachat && (
        <>
          <section className="rounded-2xl border border-[var(--color-border)] p-5 sm:p-7">
            <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
              2. Опыт
            </p>
            <ol className="mb-3 list-decimal space-y-1 pl-5">
              {opyt.shagi.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
            <p className="mb-4 text-sm text-[var(--color-text-muted)]">Смотрим: {opyt.smotret}</p>
            <p
              className={`mb-4 rounded-xl px-4 py-2 text-sm font-medium ${
                itog.gotovo
                  ? 'bg-[var(--color-success-500)]/15 text-[var(--color-success-400)]'
                  : 'bg-[var(--color-surface)]'
              }`}
              aria-live="polite"
            >
              {itog.gotovo ? '✓ ' : ''}
              {itog.hod}
            </p>
            <iframe
              ref={frejm}
              key={seed}
              title={`Аквариум: ${opyt.nazvanie}`}
              src={`${ADRES_AKVARIUMA}?opyt=${opyt.scenarij}&seed=${String(seed)}&vstroen=1`}
              allow="fullscreen; autoplay"
              allowFullScreen
              onLoad={podognatVysotu}
              style={vysota ? { height: `${String(vysota)}px` } : undefined}
              className="h-[min(78vh,760px)] w-full rounded-xl border border-[var(--color-border)]"
            />
            <div className="mt-3 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => {
                  setSobytiya([]);
                  setSeed(novyjSeed());
                }}
                className={VTORAYA}
              >
                <RotateCcw className="h-4 w-4" /> Новый аквариум
              </button>
            </div>
          </section>

          {itog.gotovo && (
            <section
              ref={blokItoga}
              className="scroll-mt-24 rounded-2xl border border-[var(--color-accent-500)] p-5 sm:p-7"
            >
              <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
                3. Что получилось
              </p>
              <ul className="mb-5 space-y-1">
                {itog.rezultat.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
              <p
                className={`mb-5 flex items-start gap-2 rounded-xl p-4 ${
                  sovpalo ? 'bg-[var(--color-success-500)]/15' : 'bg-[var(--color-warning-500)]/15'
                }`}
              >
                {sovpalo ? (
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-[var(--color-success-400)]" />
                ) : (
                  <X className="mt-0.5 h-5 w-5 shrink-0 text-[var(--color-warning-400)]" />
                )}
                <span>
                  Ваше предсказание: «{tekstPredskazaniya}».{' '}
                  {sovpalo
                    ? 'Совпало с тем, что получилось в этом аквариуме.'
                    : `В этом аквариуме вышло иначе: «${tekstVernogo ?? ''}». Ошибиться в предсказании — нормально, ради этого опыт и ставят.`}
                </span>
              </p>
              <p className="mb-5 text-sm text-[var(--color-text-muted)]">
                Для сравнения, в других аквариумах: {opyt.obychno}
              </p>
              <h3 className="mb-2 font-bold">Вывод</h3>
              <div className="mb-5 space-y-2">
                {opyt.vyvod.map((v) => (
                  <p key={v}>{v}</p>
                ))}
              </div>
              <h3 className="mb-2 font-bold">В живом мире</h3>
              <p className="mb-5">{opyt.zhivojMir}</p>
              {opyt.mostikII && (
                <p className="mb-5 flex items-start gap-2 rounded-xl bg-[var(--color-surface)] p-4">
                  <Lightbulb className="mt-0.5 h-5 w-5 shrink-0 text-[var(--color-accent-400)]" />
                  <span>
                    <b>Мостик к ИИ.</b> {opyt.mostikII}
                  </span>
                </p>
              )}
              {sleduyushchij && (
                <Link href={putOpyta(sleduyushchij)} className={GLAVNAYA}>
                  Следующий опыт: {sleduyushchij.nazvanie} <ArrowRight className="h-5 w-5" />
                </Link>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}
